/**
 * PullToRefreshIndicator — the spinner that follows the finger
 * during a pull-down. Renders inside the scroll container, fixed
 * to the top, translated down by `pullY` px. Returns null when
 * idle so it doesn't take up layout space.
 *
 * Pair with `usePullToRefresh` from /hooks/usePullToRefresh.
 */
import React from 'react';
import { Loader2, ArrowDown } from 'lucide-react';

const PullToRefreshIndicator = ({ pullY, refreshing, threshold = 70 }) => {
  if (pullY === 0 && !refreshing) return null;
  const progress = Math.min(pullY / threshold, 1);
  const ready = progress >= 1;

  return (
    <div
      className="pointer-events-none fixed left-1/2 -translate-x-1/2 z-[60] flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-[#1a1a1a] shadow-md border border-gray-200 dark:border-[#2a2a2a]"
      style={{
        top: 'calc(3.5rem + env(safe-area-inset-top, 0px) + 8px)',
        transform: `translate(-50%, ${pullY}px)`,
        opacity: refreshing ? 1 : Math.max(progress, 0.3),
        transition: refreshing ? 'transform 200ms ease-out' : 'none',
      }}
      data-testid="pull-to-refresh-indicator"
      aria-hidden="true"
    >
      {refreshing ? (
        <Loader2 className="w-4 h-4 animate-spin text-[#586D52]" />
      ) : (
        <ArrowDown
          className={`w-4 h-4 transition-transform ${ready ? 'text-[#586D52] rotate-180' : 'text-muted-foreground'}`}
        />
      )}
    </div>
  );
};

export default PullToRefreshIndicator;
